/**
 * 道路指标 API
 */
import { gapiRequest } from './request'
import type {
  TangentIndex,
  TangentIndexCalculateWeeklyRequest,
  TangentIndexCalculateWeeklyResponse,
  TangentIndexCreateRequest,
  TangentIndexUpdateRequest,
  TangentIndexListResponse,
  TangentIndexQueryParams,
  TangentIndexFluctuationQueryParams,
  TangentIndexFluctuationResponse,
  TangentIndexMapPointDetailResponse,
  TangentIndexMapPointListResponse,
  DecayPredictionRequest,
  DecayPredictionResponse,
  DecayConfigResponse,
  GradeConfigResponse,
} from '@/types/tangentIndex'

type TangentIndexFilterParams = Omit<Partial<TangentIndexQueryParams>, 'road_id'> & {
  road_id?: string | string[]
}

// 波动校验筛选参数：road_id 支持数组（由 serializeFilterParams 拼成逗号）。
type TangentIndexFluctuationFilterParams = Omit<TangentIndexFluctuationQueryParams, 'road_id'> & {
  road_id?: string | string[]
}

const serializeFilterParams = (params?: TangentIndexFilterParams) => ({
  ...params,
  road_id: Array.isArray(params?.road_id) ? params.road_id.join(',') : params?.road_id,
})

export const tangentIndexApi = {
  // 获取全量数据（不分页，列表页导出用） -> Go gapi
  getAll: (params?: TangentIndexFilterParams) =>
    gapiRequest.get<any, TangentIndex[]>('/v1/tangent-index/all', { params: serializeFilterParams(params) }),

  // 获取按百米桩最新去重后的全量数据（不分页）
  getLatestAll: (params?: TangentIndexFilterParams) =>
    gapiRequest.get<any, TangentIndex[]>('/v1/tangent-index/latest-all', { params: serializeFilterParams(params) }),

  // 导出全路网全部百米桩的衰变预测（含 15 年曲线），供性能指标预测页"导出所有"按钮使用；
  // 与道路筛选/表格勾选无关，始终导出全部数据
  exportDecayPredictionAll: () =>
    gapiRequest.get<any, Blob>('/v1/tangent-index/decay-prediction/export-all', {
      responseType: 'blob',
      timeout: 120000,
    }),

  // 获取列表
  getList: (params: TangentIndexQueryParams) =>
    gapiRequest.get<any, TangentIndexListResponse>('/v1/tangent-index', { params }),

  // 获取路段指标波动校验清单（相邻两期 PCI/PPI 相对变化达阈值的百米桩）
  getFluctuations: (params?: TangentIndexFluctuationFilterParams) =>
    gapiRequest.get<any, TangentIndexFluctuationResponse>('/v1/tangent-index/fluctuations', {
      params: serializeFilterParams(params),
    }),

  // 获取详情
  getById: (id: string) =>
    gapiRequest.get<any, TangentIndex>(`/v1/tangent-index/${id}`),

  // 获取全站衰变/合成常量（95/40/15/60）与按道路等级分档的 PPI 权重；前端以此为权威源，不再硬编码
  getDecayConfig: () =>
    gapiRequest.get<any, DecayConfigResponse>('/v1/tangent-index/decay-config'),

  // 获取全站分级阈值（优/良/中/差 边界）；前端着色/分级以此为权威源，不再硬编码
  getGradeConfig: () =>
    gapiRequest.get<any, GradeConfigResponse>('/v1/tangent-index/grade-config'),

  // 获取日期参数选项
  getDateParamOptions: () =>
    gapiRequest.get<any, Array<{ value: string; label: string }>>('/v1/tangent-index/date-params'),

  // 获取首页地图月度指标撒点；按"就近原则"只传最窄的一个筛选条件：
  // 具体路段 tangent_ids > 道路 road_id > 地图数据区域 contract_codes；date_param 为统计周期。
  // 不传 = 该条件不参与查询。tangent_ids 传空数组 = 显式"筛选结果为空"。
  getMapPoints: (params?: {
    tangent_ids?: string[]
    road_ids?: string[]
    contract_codes?: string[]
    date_params?: string[]
  }) => {
    const query: Record<string, string> = {}
    if (params?.tangent_ids) query.tangent_ids = params.tangent_ids.join(',')
    if (params?.road_ids?.length) query.road_ids = params.road_ids.join(',')
    if (params?.contract_codes?.length) query.contract_codes = params.contract_codes.join(',')
    if (params?.date_params?.length) query.date_params = params.date_params.join(',')
    return gapiRequest.get<any, TangentIndexMapPointListResponse>('/v1/tangent-index/map-points', {
      params: Object.keys(query).length ? query : undefined,
    })
  },

  // 获取单个百米桩撒点详情（首页周度卡片弹出时按 hectometer_id 拉取，与撒点列表同口径）
  // 响应外层包一层 news，后续可在同级追加其他数据块。
  getMapPointDetail: (hectometerId: string, params?: { date_param?: string }) =>
    gapiRequest.get<any, TangentIndexMapPointDetailResponse>(
      `/v1/tangent-index/map-points/${encodeURIComponent(hectometerId)}`,
      { params }
    ),

  // 手动计算单个百米桩周度指标
  calculateWeekly: (data: TangentIndexCalculateWeeklyRequest) =>
    gapiRequest.post<any, TangentIndexCalculateWeeklyResponse>(
      '/v1/tangent-index/calculate-weekly',
      data,
      { timeout: 60000 }
    ),

  // 确认提交单个百米桩周度指标计算结果
  submitWeekly: (data: TangentIndexCalculateWeeklyRequest) =>
    gapiRequest.post<any, TangentIndexCalculateWeeklyResponse>(
      '/v1/tangent-index/submit-weekly',
      data,
      { timeout: 60000 }
    ),

  // 性能指标预测：计算设计寿命衰变曲线(第0年=95)+当前等效年龄(不落库)
  decayPrediction: (data: DecayPredictionRequest) =>
    gapiRequest.post<any, DecayPredictionResponse>(
      '/v1/tangent-index/decay-prediction',
      data,
      { timeout: 60000 }
    ),

  // 创建
  create: (data: TangentIndexCreateRequest) =>
    gapiRequest.post<any, TangentIndex>('/v1/tangent-index', data),

  // 更新
  update: (id: string, data: TangentIndexUpdateRequest) =>
    gapiRequest.put<any, TangentIndex>(`/v1/tangent-index/${id}`, data),

  // 删除
  delete: (id: string) =>
    gapiRequest.delete<any, void>(`/v1/tangent-index/${id}`),
}
